import type { PixelEvent } from '@/events';
import type { PixelLogger } from '@/logger';
import type { IntegrationsModuleConfig } from '@/config/types/modules/integrations';

import {
  ConsoleDestination,
  MemoryDestination,
  MockDestination,
} from './adapters';
import {
  MetaBrowserDestination,
  GoogleBrowserDestination,
  TikTokBrowserDestination,
} from './adapters/browser';
import type { DestinationAdapter } from './types';
import type {
  IntegrationDebugState,
  DestinationHooks,
  DestinationResult,
} from './types';
import { DestinationDispatcher } from './DestinationDispatcher';
import { DestinationErrorHandler } from './DestinationErrorHandler';
import { DestinationExecutor } from './DestinationExecutor';
import { DestinationLogger } from './DestinationLogger';
import { DestinationMetrics } from './DestinationMetrics';
import { DestinationPipeline } from './DestinationPipeline';
import { DestinationRegistry } from './DestinationRegistry';

export type IntegrationEngineConfig = {
  readonly moduleConfig: IntegrationsModuleConfig;
  readonly getSessionId: () => string;
};

/**
 * Browser destinations fan-out only.
 * Does NOT collect attribution, enrich events, or talk to Transport/API.
 */
export class IntegrationEngine {
  private readonly registry: DestinationRegistry;

  private readonly metrics: DestinationMetrics;

  private readonly logger: DestinationLogger;

  private readonly pipeline: DestinationPipeline;

  private readonly dispatcher: DestinationDispatcher;

  private memoryDestination: MemoryDestination | null = null;

  private active = false;

  private readonly debug: boolean;

  constructor(
    pixelLogger: PixelLogger,
    config: IntegrationEngineConfig,
    hooks?: DestinationHooks,
    extraAdapters: readonly DestinationAdapter[] = []
  ) {
    this.registry = new DestinationRegistry();
    this.metrics = new DestinationMetrics();
    this.logger = new DestinationLogger(pixelLogger);
    this.pipeline = DestinationPipeline.identity();
    this.debug = config.moduleConfig.debug;

    const errorHandler = new DestinationErrorHandler(
      this.logger,
      config.moduleConfig.continueOnError
    );
    const executor = new DestinationExecutor({
      logger: this.logger,
      errorHandler,
      hooks,
      timeoutMs: config.moduleConfig.timeoutMs,
      debug: config.moduleConfig.debug,
    });

    this.dispatcher = new DestinationDispatcher({
      registry: this.registry,
      executor,
      metrics: this.metrics,
      logger: this.logger,
      concurrency: config.moduleConfig.concurrency,
    });

    const destinations = config.moduleConfig.destinations;
    const debug = config.moduleConfig.debug;

    if (destinations.console.enabled) {
      this.registry.register(
        new ConsoleDestination({
          log: debug
            ? (message, payload) => this.logger.debug(message, payload)
            : () => undefined,
        })
      );
    }

    if (destinations.memory.enabled) {
      this.memoryDestination = new MemoryDestination();
      this.registry.register(this.memoryDestination);
    }

    if (destinations.mock.enabled) {
      this.registry.register(new MockDestination());
    }

    if (destinations.meta.enabled && destinations.meta.pixelIds.length > 0) {
      this.registry.register(
        new MetaBrowserDestination({
          config: destinations.meta,
          getSessionId: config.getSessionId,
          logger: this.logger,
          metrics: this.metrics,
          debug,
        })
      );
    }

    if (destinations.google.enabled && destinations.google.tagIds.length > 0) {
      this.registry.register(
        new GoogleBrowserDestination({
          config: destinations.google,
          getSessionId: config.getSessionId,
          logger: this.logger,
          metrics: this.metrics,
          debug,
        })
      );
    }

    if (
      destinations.tiktok.enabled &&
      destinations.tiktok.pixelIds.length > 0
    ) {
      this.registry.register(
        new TikTokBrowserDestination({
          config: destinations.tiktok,
          getSessionId: config.getSessionId,
          logger: this.logger,
          metrics: this.metrics,
          debug,
        })
      );
    }

    for (const adapter of extraAdapters) {
      this.registry.register(adapter);
    }

    this.metrics.setRegisteredCount(this.registry.size());
  }

  init(): void {
    if (this.active) return;
    this.active = true;
    if (this.debug) {
      this.logger.debug('IntegrationEngine initialized (browser destinations)');
    }
  }

  destroy(): void {
    if (!this.active) return;
    this.registry.clear();
    this.metrics.reset();
    this.dispatcher.clearLastResults();
    this.memoryDestination = null;
    this.active = false;
    if (this.debug) {
      this.logger.debug('IntegrationEngine destroyed');
    }
  }

  isActive(): boolean {
    return this.active;
  }

  handleEvent(event: PixelEvent): void {
    if (!this.active) return;

    if (event.type === 'PAGE_VIEW') {
      this.metrics.recordPageViewReceived();
    }

    const processed = this.pipeline.process(event);
    if (!processed) {
      if (this.debug) {
        this.logger.debug(
          `Event dropped by destination pipeline: ${event.type}`
        );
      }
      return;
    }

    void this.dispatcher.dispatch(processed);
  }

  async handleEventAsync(
    event: PixelEvent
  ): Promise<readonly DestinationResult[]> {
    if (!this.active) return [];

    if (event.type === 'PAGE_VIEW') {
      this.metrics.recordPageViewReceived();
    }

    const processed = this.pipeline.process(event);
    if (!processed) return [];

    return this.dispatcher.dispatch(processed);
  }

  registerDestination(adapter: DestinationAdapter): void {
    this.registry.register(adapter);
    this.metrics.setRegisteredCount(this.registry.size());
  }

  removeDestination(name: string): boolean {
    const removed = this.registry.remove(name);
    this.metrics.setRegisteredCount(this.registry.size());
    return removed;
  }

  getRegistry(): DestinationRegistry {
    return this.registry;
  }

  getMemoryRecords() {
    return this.memoryDestination?.getRecords() ?? [];
  }

  getDebugState(): IntegrationDebugState {
    return {
      active: this.active,
      destinationNames: this.registry.getAll().map(adapter => adapter.name),
      metrics: this.metrics.getSnapshot(),
      lastResults: this.dispatcher.getLastResults(),
    };
  }
}
